import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Star, RefreshCw, Loader2, MessageSquareText, Send, Reply, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin panel showing all current ratings for Vidente Virtual sessions
 * plus the full edit history (every star/comment change is logged).
 */
export function AdminVidenteRatingsTab() {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loadingState, setLoadingState] = useState("loading"); // loading | ready | error
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoadingState("loading");
    try {
      const res = await axios.get(`${API}/admin/vidente/ratings`, { headers: authHeader() });
      setData(res.data);
      setLoadingState("ready");
    } catch {
      setLoadingState("error");
    }
  }, [authHeader]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/vidente/ratings`, { headers: authHeader() });
        if (cancelled) return;
        setData(res.data);
        setLoadingState("ready");
      } catch {
        if (!cancelled) setLoadingState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [authHeader]);

  if (loadingState === "loading" && !data) {
    return (
      <div className="text-center py-10 text-neutral-500">
        <Loader2 className="w-5 h-5 animate-spin inline" /> Cargando…
      </div>
    );
  }
  if (!data) return null;

  const { current = [], history = [], stats = {} } = data;

  const submitReply = async (sessionId, text) => {
    try {
      await axios.post(`${API}/admin/vidente/ratings/${sessionId}/reply`, { text }, { headers: authHeader() });
      toast.success(text.trim() ? "Respuesta enviada" : "Respuesta eliminada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar la respuesta");
    }
  };

  // Group history by session
  const historyBySession = history.reduce((acc, h) => {
    (acc[h.session_id] = acc[h.session_id] || []).push(h);
    return acc;
  }, {});

  return (
    <div data-testid="admin-vidente-ratings" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-xl text-white">Calificaciones · Vidente Virtual</h2>
          <p className="text-xs text-neutral-400 mt-1">
            {stats.count || 0} sesiones calificadas · promedio <span className="text-[#d4af37] font-bold">{stats.avg_stars || 0}/5</span>
          </p>
        </div>
        <button
          data-testid="ratings-refresh"
          onClick={load}
          className="text-xs px-3 py-2 rounded-full border border-white/10 hover:border-[#d4af37]/40 text-neutral-300 hover:text-[#d4af37] flex items-center gap-1.5"
        >
          <RefreshCw className="w-3 h-3" /> Recargar
        </button>
      </div>

      {current.length === 0 ? (
        <div className="text-sm text-neutral-500 text-center py-10 border border-white/5 rounded-xl">
          Aún no hay calificaciones.
        </div>
      ) : (
        <div className="space-y-3">
          {current.map((r) => (
            <div
              key={r.session_id}
              data-testid={`rating-card-${r.session_id}`}
              className="bg-black/40 border border-white/10 rounded-xl p-4 hover:border-[#d4af37]/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm text-white font-semibold">
                    {r.name || r.username || "Anónimo"}
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-neutral-500">@{r.username}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={`w-4 h-4 ${n <= (r.stars || 0) ? "text-[#d4af37] fill-[#d4af37]" : "text-neutral-700"}`}
                      />
                    ))}
                    <span className="text-xs text-[#d4af37] ml-1">{r.stars}/5</span>
                  </div>
                </div>
                <div className="text-[10px] text-neutral-500">
                  {r.updated_at ? new Date(r.updated_at).toLocaleString() : ""}
                </div>
              </div>

              {r.comment && (
                <div className="mt-2 text-sm text-neutral-200 bg-black/30 border border-white/5 rounded-lg p-3 flex items-start gap-2">
                  <MessageSquareText className="w-3.5 h-3.5 mt-0.5 text-neutral-500 flex-shrink-0" />
                  <span>{r.comment}</span>
                </div>
              )}

              <AdminReplyEditor
                sessionId={r.session_id}
                existing={r.admin_reply}
                existingAt={r.admin_reply_at}
                existingBy={r.admin_reply_by}
                onSubmit={submitReply}
              />

              {(historyBySession[r.session_id]?.length || 0) > 1 && (
                <button
                  data-testid={`rating-history-toggle-${r.session_id}`}
                  onClick={() => setExpanded((s) => ({ ...s, [r.session_id]: !s[r.session_id] }))}
                  className="mt-3 text-xs text-sky-400 hover:text-sky-300"
                >
                  {expanded[r.session_id] ? "Ocultar" : "Ver"} historial ({historyBySession[r.session_id].length} cambios)
                </button>
              )}

              {expanded[r.session_id] && (
                <div className="mt-3 space-y-2 pl-4 border-l border-white/10">
                  {historyBySession[r.session_id].map((h, i) => (
                    <div key={i} className="text-xs">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`w-3 h-3 ${n <= (h.stars || 0) ? "text-[#d4af37] fill-[#d4af37]" : "text-neutral-700"}`}
                          />
                        ))}
                        <span className="text-neutral-500 ml-2">{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                      </div>
                      {h.comment && <div className="text-neutral-400 mt-0.5">&ldquo;{h.comment}&rdquo;</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Inline editor for the admin reply on a rating                              */
/* -------------------------------------------------------------------------- */
const AdminReplyEditor = ({ sessionId, existing, existingAt, existingBy, onSubmit }) => {
  const [editing, setEditing] = useState(!existing);
  const [text, setText] = useState(existing || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setText(existing || "");
    setEditing(!existing);
  }, [existing]);

  const save = async () => {
    setBusy(true);
    try {
      await onSubmit(sessionId, text);
      if (text.trim()) setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar tu respuesta?")) return;
    setBusy(true);
    try {
      await onSubmit(sessionId, "");
      setText("");
      setEditing(true);
    } finally {
      setBusy(false);
    }
  };

  if (!editing && existing) {
    return (
      <div className="mt-3 rounded-lg border border-[#d4af37]/30 bg-[#d4af37]/5 p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest text-[#d4af37] flex items-center gap-1">
            <Reply className="w-3 h-3" /> Tu respuesta {existingBy ? `(${existingBy})` : ""}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid={`admin-reply-edit-${sessionId}`}
              onClick={() => setEditing(true)}
              className="text-[10px] uppercase tracking-widest text-neutral-300 hover:text-[#d4af37] px-2 py-0.5 rounded border border-white/10"
            >
              Editar
            </button>
            <button
              type="button"
              data-testid={`admin-reply-delete-${sessionId}`}
              onClick={remove}
              disabled={busy}
              className="text-[10px] uppercase tracking-widest text-red-300 hover:text-red-200 px-2 py-0.5 rounded border border-red-500/30 disabled:opacity-50"
            >
              <Trash2 className="w-3 h-3 inline" />
            </button>
          </div>
        </div>
        <div className="text-sm text-neutral-100 whitespace-pre-wrap">{existing}</div>
        {existingAt && (
          <div className="text-[10px] text-neutral-500 mt-1">{new Date(existingAt).toLocaleString()}</div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3 space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-[#d4af37] flex items-center gap-1">
        <Reply className="w-3 h-3" /> {existing ? "Editando respuesta" : "Responder al usuario"}
      </div>
      <Textarea
        data-testid={`admin-reply-text-${sessionId}`}
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Escribe un mensaje para el usuario (lo verá en su próxima visita a la Vidente Virtual)..."
        className="bg-black/40 border-white/10 text-white text-sm"
      />
      <div className="flex items-center justify-end gap-2">
        {existing && (
          <button
            type="button"
            onClick={() => { setEditing(false); setText(existing); }}
            disabled={busy}
            className="text-[10px] uppercase tracking-widest text-neutral-300 hover:text-white px-3 py-1.5 rounded-full border border-white/10"
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          data-testid={`admin-reply-save-${sessionId}`}
          onClick={save}
          disabled={busy || !text.trim()}
          className="btn-gold px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          {existing ? "Actualizar" : "Enviar"}
        </button>
      </div>
    </div>
  );
};
